// УЗЕ СЕС Лучки по обладнанню: блок УЗЕ → батарея Jinko → пара PCS Kehua.
//
// Структура — за ТЗ EMS box (ред. 3.1, § 2.2): кожна з 8 батарей має PCS A
// (стійки 1–6) і PCS B (стійки 7–11). Позначення PCS (T1.1…T4.4) НЕ збігається
// з номером батареї в групах T3/T4 (5.GB = T3.3/T3.4, 6.GB = T3.1/T3.2), тому
// пари беремо з адресної відомості, а не з порядку IP. Розбіжність між
// відомістю і тим, як колектор групує PCS, показуємо явно.
//
// Перевірки, які видно оку диспетчера:
//  · обидва PCS однієї батареї мають один напрямок (ТЗ, § 6.4) — протилежні
//    напрямки в парі позначаються як порушення;
//  · невідомий SOC/потужність — «—», а не нуль;
//  · вік даних кожного PCS — окремо.
import { cx } from '@/i18n/cfgText';
import i18n from '@/i18n';
import { scada } from '@/scada/tokens';
import { SectionCard } from '@/components/scada/kit';
import type { BessFleetData, BessUnit, BessBattery } from '@/scada/useBess';
import { LUCHKY_BESS, LUCHKY_BESS_BLOCKS, type LuchkyBess, type LuchkyPcs } from '@/scada/equipment/luchky';
import {
  useSharedPoll, cadenceOf, linkOf, ageSecOfLocal, clockOfLocal, fmtAge, kw0, n1, signed0, LINK_STYLE,
} from '@/scada/live';

const CAD = cadenceOf('luchky');

const PCS_STATUS: Record<string, { label: string; c: string }> = {
  off: { get label() { return i18n.t('ak_scada2_off'); }, c: scada.textMuted },
  standby: { get label() { return i18n.t('ak_scada2_standby'); }, c: scada.textDim },
  idle: { get label() { return i18n.t('ak_scada2_standby'); }, c: scada.textDim },
  charging: { get label() { return i18n.t('ak_scada2_charging'); }, c: scada.state.curtailed },
  discharging: { get label() { return i18n.t('ak_scada2_discharging'); }, c: scada.state.ok },
  running: { get label() { return i18n.t('ak_scada2_running_2'); }, c: scada.state.ok },
  normal: { get label() { return i18n.t('ak_scada2_normal'); }, c: scada.state.ok },
  fault: { get label() { return i18n.t('ak_scada2_alarm'); }, c: scada.state.alarm },
  offline: { get label() { return i18n.t('ak_scada2_no_link'); }, c: scada.state.offline },
};
const pcsStatus = (s?: string | null) => (s && PCS_STATUS[s]) || { label: s ?? '—', c: scada.textDim };

type UnitWithTime = BessUnit & { readAtLocal?: string | null };

/** Напрямок за знаком потужності PCS: + розряд, − заряд. */
const dirOf = (kw: number | null | undefined): 1 | -1 | 0 | null =>
  kw == null ? null : kw > 0.5 ? 1 : kw < -0.5 ? -1 : 0;

function PcsRow({ pcs, u }: { pcs: LuchkyPcs; u?: UnitWithTime }) {
  const age = ageSecOfLocal(u?.readAtLocal);
  const link = u ? linkOf(age, CAD) : 'unknown';
  const fresh = link === 'online';
  const st = u ? (u.alarmActive ? PCS_STATUS.fault : pcsStatus(u.status)) : PCS_STATUS.offline;
  return (
    <div
      className="grid items-center"
      style={{
        // minmax(0,…) — щоб на 375 px рядок переносив текст, а не вилазив за картку.
        gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,.8fr) minmax(0,.7fr) minmax(0,1.2fr)',
        gap: 8, padding: '7px 0', borderTop: `1px solid ${scada.border}`, opacity: fresh ? 1 : 0.65,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ color: scada.text, fontSize: 13, fontWeight: 700 }}>
          PCS {pcs.side} · {pcs.name}
        </div>
        <div style={{ color: scada.textMuted, fontSize: 10 }}>{i18n.t('ak_scada2_racks', { r: pcs.racks, ip: pcs.ip })}</div>
      </div>
      <div style={{ color: st.c, fontSize: 12, fontWeight: 600 }}>{st.label}</div>
      <div style={{ color: scada.text, fontSize: 13, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
        {u && fresh ? signed0(u.powerKw) : '—'}<span style={{ color: scada.textMuted, fontSize: 10 }}> {i18n.t('ak_scada2_kw')}</span>
      </div>
      <div style={{ color: scada.textMuted, fontSize: 10, textAlign: 'right', lineHeight: 1.35 }}>
        {u ? (
          <>
            {i18n.t('ak_scada2_limit_kw_heatsink', { a: kw0(u.maxChargeKw ?? u.maxDischargeKw), b: n1(u.radiatorTempC) })}
            <br />
            <span style={{ color: LINK_STYLE[link].c }}>{clockOfLocal(u.readAtLocal)} · {fmtAge(age)}</span>
          </>
        ) : i18n.t('ak_scada2_no_data_from_the_collector')}
      </div>
    </div>
  );
}

function BatteryCard({ b, battery, units }: { b: LuchkyBess; battery?: BessBattery; units: Map<string, UnitWithTime> }) {
  const [a, bb] = b.pcs;
  const ua = units.get(a.name);
  const ub = units.get(bb.name);
  const da = dirOf(ua?.powerKw);
  const db = dirOf(ub?.powerKw);
  const opposite = da != null && db != null && da * db === -1;
  const sumKw = ua?.powerKw != null || ub?.powerKw != null ? (ua?.powerKw ?? 0) + (ub?.powerKw ?? 0) : null;
  // Пара з колектора має збігатися з адресною відомістю ТЗ.
  const pairOk = !battery || battery.pcsUnits.length === 0
    || (battery.pcsUnits.includes(a.name) && battery.pcsUnits.includes(bb.name));
  const alarm = !!(ua?.alarmActive || ub?.alarmActive);

  return (
    <div style={{ border: `1px solid ${alarm || opposite || !pairOk ? `${scada.state.warning}80` : scada.border}`, borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
      <div className="flex items-start justify-between" style={{ gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: scada.text, fontSize: 15, fontWeight: 700 }}>{i18n.t('ak_scada2_battery')} {b.designation}</div>
          <div style={{ color: scada.textMuted, fontSize: 10 }}>Jinko SCU {b.scuIp} · S/N {b.serial}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ color: scada.text, fontSize: 20, fontWeight: 700, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
            {n1(battery?.socPct ?? null)}<span style={{ color: scada.textMuted, fontSize: 11, fontWeight: 400 }}> % SOC</span>
          </div>
          <div style={{ color: scada.textMuted, fontSize: 10, marginTop: 3 }}>
            Σ {signed0(sumKw)} {i18n.t('ak_scada2_kw')}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap" style={{ gap: '4px 12px', color: scada.textMuted, fontSize: 11, margin: '8px 0 4px' }}>
        <span>SOH {n1(battery?.sohPct ?? null)} %</span>
        <span>{i18n.t('ak_scada2_cell_spread')} {battery?.cellVspreadMv != null ? `${battery.cellVspreadMv} ${i18n.t('ak_scada2_mv')}` : '—'}</span>
        <span>T {n1(battery?.tempMinC ?? null)}…{n1(battery?.tempMaxC ?? null)} °C</span>
        <span>{i18n.t('ak_scada2_pack')} {kw0(battery?.packV ?? null)} {i18n.t('ak_scada2_v')}</span>
      </div>

      {(opposite || !pairOk || alarm) && (
        <div style={{ color: scada.state.warning, fontSize: 11, margin: '4px 0' }}>
          {alarm && i18n.t('ak_scada2_active_pcs_alarm')}
          {opposite && i18n.t('ak_scada2_the_pcs_of_the_pair_operate_in_opposit')}
          {!pairOk && i18n.t('ak_scada2_the_collector_groups_this_battery_with', { p: battery?.pcsUnits.join(', '), a: a.name, b: bb.name })}
        </div>
      )}

      <PcsRow pcs={a} u={ua} />
      <PcsRow pcs={bb} u={ub} />
    </div>
  );
}

export default function LuchkyBessBoard({ assetId }: { assetId: string }) {
  const { data, err } = useSharedPoll<BessFleetData>(`/scada/bess/${assetId}/latest`, 30000);
  if (!data) {
    return (
      <SectionCard title={i18n.t('ak_scada2_bess_by_equipment')}>
        <span style={{ color: scada.textMuted, fontSize: 13 }}>{err ? i18n.t('ak_scada2_bess_data_is_currently_unavailable') : i18n.t('ak_scada2_loading')}</span>
      </SectionCard>
    );
  }

  const units = new Map(data.units.map((u) => [u.pcsName, u as UnitWithTime]));
  const batteries = new Map(data.batteries.map((b) => [b.batteryId, b]));
  const s = data.summary;
  const batOnline = data.batteries.filter((b) => b.socPct != null).length;
  const age = ageSecOfLocal(data.readAtLocal);

  return (
    <SectionCard
      title={i18n.t('ak_scada2_bess_by_equipment')}
      subtitle={i18n.t('ak_scada2_4_blocks_2_batteries_pcs_a_b_sign_disc', { c: clockOfLocal(data.readAtLocal), a: fmtAge(age) })}
      right={
        <span style={{ color: scada.textMuted, fontSize: 11, whiteSpace: 'nowrap' }}>
          {i18n.t('ak_scada2_batteries')} <b style={{ color: batOnline === LUCHKY_BESS.length ? scada.state.ok : scada.state.warning }}>{batOnline}/{LUCHKY_BESS.length}</b>
          {' · '}PCS <b style={{ color: s.online === data.count ? scada.state.ok : scada.state.warning }}>{s.online}/{data.count}</b>
          {s.alarmsActive > 0 && <b style={{ color: scada.state.alarm }}>{i18n.t('ak_scada2_alarms', { n: s.alarmsActive })}</b>}
        </span>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5" style={{ marginBottom: 14 }}>
        {[
          { label: i18n.t('ak_scada2_bess_power'), value: `${signed0(s.powerKw)} ${i18n.t('ak_scada2_kw')}` },
          { label: i18n.t('ak_scada2_system_soc'), value: `${n1(s.socPct)} %` },
          { label: i18n.t('ak_scada2_can_charge'), value: `${kw0(s.chargeAvailableKwh)} ${i18n.t('ak_scada2_kwh')}` },
          { label: i18n.t('ak_scada2_can_discharge'), value: `${kw0(s.dischargeAvailableKwh)} ${i18n.t('ak_scada2_kwh')}` },
        ].map((m) => (
          <div key={m.label} style={{ background: scada.panel2, borderRadius: 8, padding: '8px 10px' }}>
            <div style={{ color: scada.textMuted, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.04em' }}>{m.label}</div>
            <div style={{ color: scada.text, fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{m.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {LUCHKY_BESS_BLOCKS.map((blk) => {
          const blkUnits = LUCHKY_BESS.filter((b) => blk.bess.includes(b.id))
            .flatMap((b) => b.pcs.map((p) => units.get(p.name)?.powerKw))
            .filter((v): v is number => v != null);
          return (
            <div key={blk.id}>
              <div className="flex items-center justify-between flex-wrap" style={{ gap: 6, marginBottom: 6 }}>
                <span style={{ color: scada.textDim, fontSize: 12, fontWeight: 600 }}>
                  {cx(blk.name)} <span style={{ color: scada.textMuted, fontWeight: 400 }}>· {blk.model} · {cx(blk.transformer)}</span>
                </span>
                <span style={{ color: scada.textMuted, fontSize: 11 }}>
                  Σ <b style={{ color: scada.text }}>{signed0(blkUnits.length ? blkUnits.reduce((x, y) => x + y, 0) : null)}</b> {i18n.t('ak_scada2_kw_of')} ±{kw0(blk.powerKw)}
                </span>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                {LUCHKY_BESS.filter((b) => blk.bess.includes(b.id)).map((b) => (
                  <BatteryCard key={b.id} b={b} battery={batteries.get(b.id)} units={units} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ color: scada.textMuted, fontSize: 11, marginTop: 12 }}>
        {i18n.t('ak_scada2_the_collector_does_not_yet_collect_soc')}
      </div>
    </SectionCard>
  );
}
